// =====================================================
// Swiss Finance OS - Transaction Hooks
// React Query hooks for transaction data management
// =====================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { transactionService, type TransactionFilters, type DashboardMetrics } from '@/services/transactions';
import { useToast } from '@/hooks/use-toast';
import { useMandateContext } from '@/contexts/mandate-context';

export interface UseTransactionsOptions {
  page?: number;
  pageSize?: number;
  filters?: TransactionFilters;
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * Hook for fetching paginated transactions
 * Automatically filters by selected mandate from context
 */
export function useTransactions(options: UseTransactionsOptions = {}) {
  const {
    page = 1,
    pageSize = 20,
    filters = {},
    enabled = true,
    refetchInterval = false, // Disabled - use realtime updates instead
  } = options;

  const { selectedMandateId } = useMandateContext();
  const queryClient = useQueryClient();

  // Invalidate all transaction queries when mandate changes (better cache management)
  useEffect(() => {
    const handleMandateChange = () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });
    };

    window.addEventListener('mandate-changed', handleMandateChange);
    return () => window.removeEventListener('mandate-changed', handleMandateChange);
  }, [queryClient]);

  // Merge mandate filter from context with user-provided filters
  const enhancedFilters: TransactionFilters = {
    ...filters,
    mandateId: selectedMandateId || filters.mandateId,
  };

  return useQuery({
    queryKey: ['transactions', selectedMandateId, page, pageSize, enhancedFilters],
    queryFn: () => transactionService.getTransactions(page, pageSize, enhancedFilters),
    enabled: enabled && !!selectedMandateId, // Only fetch when mandate is selected
    refetchInterval,
    refetchOnWindowFocus: false, // Disabled - use realtime updates
    refetchOnReconnect: false,
    refetchOnMount: false, // Only fetch on explicit user action
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
  });
}

/**
 * Hook for dashboard metrics
 * TODO: Add mandate-level filtering to getDashboardMetrics service
 */
export function useDashboardMetrics(options: { enabled?: boolean; refetchInterval?: number | false } = {}) {
  const { enabled = true, refetchInterval = false } = options; // Disabled - use realtime
  const { selectedMandateId } = useMandateContext();

  return useQuery({
    queryKey: ['dashboard-metrics', selectedMandateId],
    queryFn: () => transactionService.getDashboardMetrics(),
    enabled: enabled && !!selectedMandateId,
    refetchInterval,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Hook for monthly revenue data
 */
export function useMonthlyRevenue(months: number = 12, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['monthly-revenue', months],
    queryFn: () => transactionService.getMonthlyRevenueData(months),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Hook for mandates list
 */
export function useMandates(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['mandates'],
    queryFn: () => transactionService.getMandates(),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Hook for updating transaction classification
 */
export function useUpdateTransaction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedMandateId } = useMandateContext();

  return useMutation({
    mutationFn: ({
      transactionId,
      updates
    }: {
      transactionId: string;
      updates: Parameters<typeof transactionService.updateTransactionClassification>[1]
    }) => {
      return transactionService.updateTransactionClassification(transactionId, updates);
    },
    onSuccess: (data) => {
      // Invalidate queries scoped by mandate for better cache management
      queryClient.invalidateQueries({ queryKey: ['transactions', selectedMandateId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-metrics', selectedMandateId] });

      toast({
        title: 'Transaction Updated',
        description: 'Transaction classification has been updated successfully.',
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update transaction classification.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for bulk approving transactions
 */
export function useBulkApproveTransactions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedMandateId } = useMandateContext();

  return useMutation({
    mutationFn: (transactionIds: string[]) => {
      return transactionService.bulkApproveTransactions(transactionIds);
    },
    onSuccess: (_, transactionIds) => {
      // Invalidate queries scoped by mandate for better cache management
      queryClient.invalidateQueries({ queryKey: ['transactions', selectedMandateId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-metrics', selectedMandateId] });

      toast({
        title: 'Transactions Approved',
        description: `Successfully approved ${transactionIds.length} transactions.`,
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk Approval Failed',
        description: error.message || 'Failed to approve transactions.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for real-time transaction updates (combines multiple data sources)
 */
export function useTransactionDashboard(options: { refetchInterval?: number | false } = {}) {
  const { refetchInterval = false } = options; // Disabled - use realtime updates

  const metrics = useDashboardMetrics({ refetchInterval });
  const revenueData = useMonthlyRevenue();
  const mandates = useMandates();

  const isLoading = metrics.isLoading || revenueData.isLoading || mandates.isLoading;
  const isError = metrics.isError || revenueData.isError || mandates.isError;

  const refresh = () => {
    metrics.refetch();
    revenueData.refetch();
    mandates.refetch();
  };

  return {
    metrics: metrics.data,
    revenueData: revenueData.data,
    mandates: mandates.data,
    isLoading,
    isError,
    refresh,
    errors: {
      metrics: metrics.error,
      revenue: revenueData.error,
      mandates: mandates.error,
    },
  };
}
